import { useState } from 'react'
import { Plus } from 'lucide-react'
import {
  Button,
  EmptyState,
  ErrorState,
  MoneyValue,
  ProductCoverPlaceholder,
  Skeleton,
  StatusBadge,
  Toggle,
} from '@shared/design-system/ui'
import { getProductStatusPresentation } from '@entities/product'
import { resolvePublicMediaUrl } from '@shared/lib/media/public-media-url'
import { productCategoryLabel, productTypeLabels } from '@entities/product/api/product.contract'
import type { Product } from '@entities/product/api/product.contract'
import {
  cardTop,
  cover,
  coverImage,
  filterBar,
  filterLabel,
  metaRow,
  muted,
  page,
  pageHeader,
  pageSubtitle,
  pageTitle,
  productCard,
  productGrid,
  productName,
} from './ProductsList.css'

export type ProductsListProps = {
  products: Product[]
  loading: boolean
  error: boolean
  canCreate?: boolean
  onCreate: () => void
  onOpen: (productId: string) => void
  onRetry: () => void
}

export function ProductsList({
  products,
  loading,
  error,
  canCreate = true,
  onCreate,
  onOpen,
  onRetry,
}: ProductsListProps) {
  const [showArchived, setShowArchived] = useState(false)
  const createDisabled = loading || error || !canCreate
  const hasAnyArchived = products.some((p) => p.status === 'ARCHIVED')
  const visibleProducts = showArchived ? products : products.filter((p) => p.status !== 'ARCHIVED')

  return (
    <div className={page} data-testid="products-page">
      <header className={pageHeader} data-testid="products-header">
        <div>
          <h1 className={pageTitle}>Produtos</h1>
          <p className={pageSubtitle}>
            Crie, publique e ajuste ofertas, funis e checkouts em uma experiência de foco.
          </p>
        </div>
        <Button
          leftIcon={<Plus size={17} />}
          disabled={createDisabled}
          onClick={onCreate}
          data-testid="products-create-btn"
        >
          Novo produto
        </Button>
      </header>

      {!loading && !error && hasAnyArchived ? (
        <div className={filterBar} data-testid="products-archive-filter">
          <label className={filterLabel} htmlFor="toggle-archived">
            Ver arquivados
          </label>
          <Toggle
            id="toggle-archived"
            checked={showArchived}
            onChange={setShowArchived}
            ariaLabel="Mostrar produtos arquivados"
            data-testid="products-archive-toggle"
          />
        </div>
      ) : null}

      {loading ? (
        <div className={productGrid} role="status" aria-label="Carregando produtos">
          <Skeleton height={260} radius="16px" />
          <Skeleton height={260} radius="16px" />
          <Skeleton height={260} radius="16px" />
        </div>
      ) : error ? (
        <ErrorState
          title="Não foi possível carregar os produtos"
          description="Tente novamente antes de criar ou editar um produto."
          onRetry={onRetry}
        />
      ) : products.length === 0 ? (
        <EmptyState
          data-testid="products-empty"
          icon={<Plus size={22} />}
          title="Nenhum produto cadastrado"
          description={
            canCreate
              ? 'Crie um rascunho para configurar preço, funil, checkout e publicação.'
              : 'A documentação precisa estar aprovada para cadastrar um produto.'
          }
          action={
            <Button leftIcon={<Plus size={17} />} disabled={!canCreate} onClick={onCreate}>
              Novo produto
            </Button>
          }
        />
      ) : visibleProducts.length === 0 ? (
        <EmptyState
          data-testid="products-all-archived"
          icon={<Plus size={22} />}
          title="Todos os produtos estão arquivados"
          description="Ative o filtro acima para visualizá-los."
          action={
            <Button variant="secondary" onClick={() => setShowArchived(true)}>
              Ver arquivados
            </Button>
          }
        />
      ) : (
        <div className={productGrid} data-testid="products-grid">
          {visibleProducts.map((product) => {
            const status = getProductStatusPresentation(product.status)
            const Icon = status.icon
            const primaryOffer =
              product.offers.find((offer) => offer.id === product.links.primaryOfferId) ??
              product.offers[0]
            return (
              <button
                key={product.id}
                type="button"
                className={productCard}
                data-testid={`product-card-${product.id}`}
                onClick={() => onOpen(product.id)}
              >
                <div className={cover}>
                  {product.coverImage ? (
                    <img className={coverImage} src={resolvePublicMediaUrl(product.coverImage)} alt="" />
                  ) : (
                    <ProductCoverPlaceholder name={product.name} />
                  )}
                </div>
                <div className={cardTop}>
                  <div>
                    <h2 className={productName}>{product.name}</h2>
                    <p className={muted}>
                      {productTypeLabels[product.productType]} ·{' '}
                      {productCategoryLabel(product.category)}
                    </p>
                  </div>
                  <StatusBadge
                    tone={status.tone}
                    label={status.label}
                    icon={<Icon size={13} />}
                    size="sm"
                  />
                </div>
                <div className={metaRow}>
                  {primaryOffer ? (
                    <MoneyValue
                      amount={primaryOffer.price.amount}
                      currency={primaryOffer.price.currency}
                      size="md"
                    />
                  ) : (
                    <span className={muted}>Sem oferta</span>
                  )}
                  <span className={muted}>{product.checkouts.length} checkout(s)</span>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
